import type * as React from "react";

export type InputMask = "phone" | "cpf" | "cnpj" | "cep";

function getDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatPhone(value: string) {
  const digits = getDigits(value).slice(0, 11);

  if (digits.length === 0) {
    return "";
  }

  if (digits.length <= 2) {
    return `(${digits}`;
  }

  const areaCode = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (digits.length <= 10) {
    if (rest.length <= 4) {
      return `(${areaCode}) ${rest}`;
    }

    return `(${areaCode}) ${rest.slice(0, 4)}-${rest.slice(4, 8)}`;
  }

  if (rest.length <= 1) {
    return `(${areaCode}) ${rest}`;
  }

  if (rest.length <= 5) {
    return `(${areaCode}) ${rest.slice(0, 1)} ${rest.slice(1)}`;
  }

  return `(${areaCode}) ${rest.slice(0, 1)} ${rest.slice(1, 5)}-${rest.slice(5, 9)}`;
}

function formatCpf(value: string) {
  const digits = getDigits(value).slice(0, 11);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  }

  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
}

function formatCnpj(value: string) {
  const digits = getDigits(value).slice(0, 14);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }

  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }

  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

function formatCep(value: string) {
  const digits = getDigits(value).slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}-${digits.slice(5, 8)}`;
}

export function applyInputMask(mask: InputMask, value: string) {
  switch (mask) {
    case "phone":
      return formatPhone(value);
    case "cpf":
      return formatCpf(value);
    case "cnpj":
      return formatCnpj(value);
    case "cep":
      return formatCep(value);
    default:
      return value;
  }
}

export function getInputMaskMode(mask: InputMask): React.HTMLAttributes<HTMLInputElement>["inputMode"] {
  switch (mask) {
    case "phone":
    case "cpf":
    case "cnpj":
    case "cep":
      return "numeric";
    default:
      return undefined;
  }
}
